<template>
  <div class="rc-select-container" v-click-outside="hide">
    <div class="rc-select" @click="selectClickHandler">
      <template v-if="floatLabel">
        <div
          ref="floating-label"
          class="rc-floating-label"
          :class="{
            'rc-label-active': getSelectedOption,
            'rc-label-centered': !getSelectedOption,
          }"
        >
          <label class="rc-floating-label__text">
            {{ getLabel }}
          </label>
          <span
            v-show="requiredField"
            class="rc-required-field"
            ref="requiredField"
            >*</span
          >
        </div>
      </template>

      <template v-if="!floatLabel && !getSelectedOption">
        <div ref="floating-label" class="rc-floating-label rc-label-centered">
          <label class="rc-floating-label__text">
            {{ getLabel }}
          </label>
        </div>
      </template>
      <div
        class="rc-select__selected-option-container"
        :title="getSelectedOption"
      >
        <label class="rc-select__selected-option">{{
          getSelectedOption
        }}</label>
      </div>
      <div class="rc-select__icon-container">
        <svg class="ionicon" viewBox="0 0 512 512">
          <path
            fill="none"
            stroke="currentColor"
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="10"
            d="M112 184l144 144 144-144"
          />
        </svg>
      </div>
    </div>
    <div class="rc-suggestions" v-if="suggestions.length && showSuggestions">
      <ul class="rc-suggestions-list" ref="suggestionsList">
        <li
          class="rc-suggestions-list__item"
          :class="
            index === hoveredSuggestionsIndex ? 'hover-item-background' : ''
          "
          @mousedown="selectSuggestion(suggestion)"
          v-for="(suggestion, index) in suggestions"
          :key="index"
        >
          <slot name="suggestion-item" :suggestion="suggestion">
            {{ suggestion.label }}
          </slot>
        </li>
      </ul>
    </div>
  </div>
</template>

<script>
export default {
  name: "RCSelect",
  props: {
    value: {
      required: false,
    },
    suggestions: {
      type: Array,
      default: () => [],
    },
    floatLabel: {
      type: Boolean,
      default: false,
    },
    label: String,
    requiredField: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  directives: {
    "click-outside": {
      bind: (el, binding, vnode) => {
        el.clickOutsideEvent = (event) => {
          if (!(el === event.target || el.contains(event.target))) {
            vnode.context[binding.expression](event);
          }
        };
        document.body.addEventListener("click", el.clickOutsideEvent);
      },
      unbind: (el) => {
        document.body.removeEventListener("click", el.clickOutsideEvent);
      },
    },
  },
  data() {
    return {
      showSuggestions: false,
      hoveredSuggestionsIndex: -1,
      selectedOption: null,
    };
  },
  watch: {
    value: {
      deep: true,
      immediate: true,
      handler(newValue) {
        this.selectedOption = this.suggestions.find(
          (suggestion) => suggestion.value === newValue
        );
      },
    },
  },
  computed: {
    getLabel() {
      return this.label || "Choose an option";
    },
    getSelectedOption() {
      if (this.selectedOption) {
        return this.selectedOption?.label || "";
      }
      if (this.value) {
        return this.value?.label || "";
      }
      return "";
    },
  },
  methods: {
    selectClickHandler() {
      this.showSuggestions = !this.showSuggestions;
    },
    selectSuggestion(suggestion) {
      this.selectedOption = suggestion;
      this.showSuggestions = false;
      this.hoveredSuggestionsIndex = -1;
      this.$emit("change", suggestion.value);
      this.$emit("getFullOption", suggestion);
    },
    hide() {
      this.showSuggestions = false;
    },
  },
};
</script>

<style scoped>
.rc-select-container {
  background-color: white;
  position: relative;
  display: flex;
  align-items: center;
  width: 200px;
  height: 40px;
  transition: all 0.3s;
  font-size: 16px;
  color: #888;
  border: 1px solid #939393;
  border-radius: 10px;
}

.ionicon {
  right: 2%;
  top: 15%;
  width: 25px;
  height: 25px;
  color: black;
}

.rc-select {
  width: 100%;
  height: 100%;
  padding-left: 5px;
  display: flex;
  justify-content: space-between;
  cursor: pointer;
}

.rc-select__selected-option-container {
  display: flex;
  align-items: center;
}

.rc-select__selected-option {
  margin-top: 2px;
  margin-bottom: 0px;
  white-space: nowrap;
  text-overflow: ellipsis;
  white-space: nowrap;
  overflow: hidden;
}

.rc-select__icon-container {
  display: flex;
  align-items: center;
}

.hover-item-background {
  background-color: #e9ecef;
}

.rc-suggestions {
  position: absolute;
  top: 40px;
  left: 0px;
  width: 100%;
  height: auto;
  max-height: 150px;
  overflow-y: scroll;
  border: 1px solid rgba(196, 196, 196, 0.767);
  background-color: white;
  z-index: 10;
}

.rc-suggestions-list {
  padding: 0;
  margin: 0;
  list-style-type: none;
  text-align: left;
  overflow-x: hidden;
}

.rc-suggestions-list__item {
  padding: 5px 10px;
  cursor: pointer;
}

.rc-suggestions-list__item:hover {
  background-color: #ededed;
}

.rc-suggestions::-webkit-scrollbar {
  width: 5px;
}
.rc-suggestions::-webkit-scrollbar-track {
  box-shadow: inset 0 0 6px rgba(0, 0, 0, 0.3);
  border-radius: 10px;
}
.rc-suggestions::-webkit-scrollbar-thumb {
  border-radius: 10px;
  box-shadow: inset 0 0 6px rgba(0, 0, 0, 0.5);
}

.rc-floating-label {
  position: absolute;
  width: 95%;
  top: 0px;
  left: 5px;
  text-align: left;
  pointer-events: none;
  transition: all 0.2s;
  font-size: 16px;
  color: #6c757d;
}

.rc-floating-label__text {
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  margin: 0px;
}

.rc-label-active {
  font-size: 12px;
  transform: translateY(-18px);
}

.rc-label-centered {
  width: 80%;
  top: 52%;
  left: 5px;
  transform: translateY(-50%);
}
</style>
